/* eslint-disable @typescript-eslint/camelcase */
// 分類・状態・理由の値は、サーバー（Cloud Functions）が返す snake_case の文字列そのまま
import {
  OrphanCategory,
  OrphanCollection,
  OrphanStatus
} from "@/entities/orphan_subcollection_finding";
import firebase from "firebase/app";
import "firebase/functions";

/** 画面に返る検知結果。時刻は ISO 文字列。 */
export type OrphanFindingJson = {
  id: string;
  path: string;
  schoolId: string;
  classroomId: string;
  /** 学校名・教室名。取れないとき（削除済みなど）は無い */
  schoolName?: string;
  classroomName?: string;
  collection: OrphanCollection;
  uid: string;
  category: OrphanCategory;
  subcollections: string[];
  rolesRefPath?: string;
  status: OrphanStatus;
  detectedAt: string;
  lastSeenAt: string;
  deletedCount?: number;
  errorMessage?: string;
  adminActions: Array<{
    action: "delete" | "fail";
    adminUid: string;
    time: string;
    deletedCount?: number;
    error?: string;
  }>;
};

export type OrphanScanRunJson = {
  startedAt: string;
  finishedAt: string;
  cycleCompleted: boolean;
  scannedSchools: number;
  scannedClassrooms: number;
  newFindings: number;
  totalFindings: number;
  byCategory: Record<OrphanCategory, number>;
  resolvedFindings: number;
};

export type ListOrphanFindingsResult = {
  findings: OrphanFindingJson[];
  latestRun: OrphanScanRunJson | null;
  state: { completedAt?: string; cycleStartedAt?: string } | null;
};

export type OrphanPreview = {
  findingId: string;
  path: string;
  schoolName?: string;
  classroomName?: string;
  category: OrphanCategory;
  subcollections: string[];
  docCount: number;
  truncated: boolean;
  deletable: boolean;
  reason?: string;
};

export type OrphanExecuteResult =
  | { executed: true; deletedCount: number }
  | { executed: false; reason: string; docCount?: number };

const callable = (name: string) =>
  firebase
    .app()
    .functions("asia-northeast1")
    .httpsCallable(name, { timeout: 540 * 1000 });

export async function listOrphanFindings(): Promise<ListOrphanFindingsResult> {
  const res = await callable("list_orphan_subcollection_findings")();
  return res.data as ListOrphanFindingsResult;
}

export async function previewOrphanDeletion(
  findingId: string
): Promise<OrphanPreview> {
  const res = await callable("preview_orphan_subcollection_deletion")({
    findingId
  });
  return res.data as OrphanPreview;
}

export async function executeOrphanDeletion(
  findingId: string,
  expectedDocCount: number
): Promise<OrphanExecuteResult> {
  const res = await callable("execute_orphan_subcollection_deletion")({
    findingId,
    expectedDocCount
  });
  return res.data as OrphanExecuteResult;
}

export const CATEGORY_LABELS: Record<OrphanCategory, string> = {
  moved_leftover: "移動済みの残骸",
  ownerless: "所有者不明",
  unmoved_only_copy: "唯一のデータ（削除不可）"
};

export const CATEGORY_DESCRIPTIONS: Record<OrphanCategory, string> = {
  moved_leftover:
    "生徒は別の教室へ移っていて、ここに残っているのは過去の移動の残骸です。削除してよい候補です。移動先に同じデータがあるかは、確認していません。旧方式の移動で移されなかったデータも含まれます。",
  ownerless:
    "この生徒の roles が見つかりません（生徒の削除などの残り）。内容を確認してから削除してください。",
  unmoved_only_copy:
    "ここが唯一のデータの可能性があります。削除できません。復旧手順で扱ってください。"
};

export const STATUS_LABELS: Record<OrphanStatus, string> = {
  detected: "検知中",
  deleting: "削除中",
  deleted: "削除済み",
  failed: "削除に失敗",
  resolved: "解消済み"
};

export const REASON_LABELS: Record<string, string> = {
  not_found: "検知結果が見つかりません",
  not_whitelisted: "削除の対象にできないパスです",
  parent_exists:
    "親のドキュメントが復活していたため、削除しませんでした（解消済みにしました）",
  nothing_left: "残っているデータがありませんでした（解消済みにしました）",
  protected:
    "唯一のデータの可能性があるため、削除できません（roles が、この教室の生徒や、見つからない生徒を指しています）",
  in_transfer: "教室移動の途中のため、削除しませんでした",
  status: "すでに削除中、または削除済みです",
  count_changed:
    "確認のあとに、データが増えていました。もう一度、内容を確認してください",
  delete_failed: "削除に失敗しました。もう一度実行できます"
};
